/**
 * EliteUpgradeModal Component
 * Shown when a 403 Elite-required error is caught by the API interceptor.
 * Never logs out - just prompts for upgrade.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, X, Lock, Sparkles } from 'lucide-react';
import { setEliteUpgradeCallback } from '../../utils/eliteGate';

const EliteUpgradeModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [featureName, setFeatureName] = useState('this feature');
  const navigate = useNavigate();

  useEffect(() => {
    // Register callback for Elite upgrade triggers
    setEliteUpgradeCallback((feature) => {
      setFeatureName(feature || 'this feature');
      setIsOpen(true);
    });

    return () => {
      setEliteUpgradeCallback(null);
    };
  }, []);

  const handleUpgrade = () => {
    setIsOpen(false);
    navigate('/shop?focus=elite');
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleClose}
      data-testid="elite-upgrade-modal"
    >
      <div
        className="surface-card rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden transform transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-yellow-500 p-6 text-white relative">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Crown className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Elite Feature</h2>
              <p className="text-amber-100 text-sm">Unlock premium access</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-amber-600" />
            </div>
          </div>

          <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
            {featureName} requires Elite
          </h3>

          <p className="text-gray-600 text-center mb-6">
            Upgrade to Elite to unlock this premium feature and many more exclusive benefits.
          </p>

          {/* Benefits */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex items-center text-gray-700 mb-2">
              <Sparkles className="w-4 h-4 text-amber-500 mr-2" />
              <span className="text-sm font-medium">Elite Benefits Include:</span>
            </div>
            <ul className="text-sm text-gray-600 space-y-1 ml-6">
              <li>• See who liked you</li>
              <li>• More likes daily</li>
              <li>• Priority matching</li>
              <li>• Elite badge on profile</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
            >
              Maybe Later
            </button>
            <button
              onClick={handleUpgrade}
              className="flex-1 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-yellow-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-yellow-600 transition-all"
              data-testid="elite-upgrade-button-modal"
            >
              <Crown className="w-4 h-4 inline mr-1" />
              Upgrade Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EliteUpgradeModal;
